import {
  MenuEmpty,
  MenuInput,
  MenuItems,
  NextButton,
  NextTooltipContent,
  Pagination,
  SearchInput,
  SelectBadge,
  SelectMenu,
  SelectMenuContent,
  SelectMenuItem,
  SelectMenuProvider,
  SelectMenuTriggerType,
  Spinner,
  Table,
  TableToolbar,
  TBody,
  TCellData,
  TextLink,
  THead,
  THeaderCell,
  THeadRow,
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  Tr,
} from '@cint/wonderment';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useInfiniteQueryData } from '@/api/apiHooks';
import { RECRUITMENT_MARKETING_PROVIDER_DETAILS } from '@/components/MarketingCampaign/components';
import useHasPermission from '@/hooks/useHasPermission';
import { RESOURCES } from '@/utils/permissions';
import {
  MarketingCampaignResponse,
  MarketingCampaignsResponse,
} from '@/utils/types';
import { URLS } from '@/utils/urls';

import {
  ALL_PROVIDERS_VALUE,
  useTrackingProviderOptions,
} from './useTrackingProviderOptions';

export function MarketingCampaignsTable({ panelId }: { panelId: string }) {
  const [selectedItems, setSelectedItems] = useState<string[]>([
    ALL_PROVIDERS_VALUE,
  ]);
  const [searchTerm, setSearchTerm] = useState('');
  const {
    options,
    isLoading: trackingOptionsLoading,
    isFetching: trackingOptionsFetching,
  } = useTrackingProviderOptions();

  const {
    isLoading: campaignsLoading,
    isFetching: campaignsFetching,
    data: campaignsPages,
    error: campaignsError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQueryData<MarketingCampaignsResponse>({
    url: URLS.getMarketingCampaigns(panelId),
  });
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const campaignsData = useMemo(
    () =>
      campaignsPages?.pages?.flatMap(
        (page: MarketingCampaignsResponse) => page.data
      ) || [],
    [campaignsPages]
  );

  const filteredCampaignsData = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    return campaignsData.filter((campaign: MarketingCampaignResponse) => {
      const matchesSearchTerm = normalizedSearchTerm
        ? campaign.name?.toLowerCase().includes(normalizedSearchTerm)
        : true;
      const matchesTracking =
        selectedItems.length && !selectedItems.includes(ALL_PROVIDERS_VALUE)
          ? selectedItems.includes(
              campaign.provider_config?.server_to_server
                ?.tracking_template_id ??
                campaign.provider ??
                ''
            )
          : true;

      return matchesSearchTerm && matchesTracking;
    });
  }, [campaignsData, searchTerm, selectedItems]);

  const handleSelectedTrackingChange = (items: string[]) => {
    const hadAllProviders = selectedItems.includes(ALL_PROVIDERS_VALUE);
    const hasAllProviders = items.includes(ALL_PROVIDERS_VALUE);

    if (hasAllProviders && !hadAllProviders) {
      // "All providers" was just checked - it's exclusive of everything else.
      setSelectedItems([ALL_PROVIDERS_VALUE]);
    } else if (hasAllProviders && items.length > 1) {
      // A specific provider was checked while "All providers" was active.
      setSelectedItems(items.filter((item) => item !== ALL_PROVIDERS_VALUE));
    } else if (items.length === 0) {
      // Nothing left selected - fall back to "All providers".
      setSelectedItems([ALL_PROVIDERS_VALUE]);
    } else {
      setSelectedItems(items);
    }
  };

  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission: canEdit } = useHasPermission({
    resource: RESOURCES.marketing.id,
    action: RESOURCES.marketing.actions.editcamp,
  });
  const { hasPermission: canView } = useHasPermission({
    resource: RESOURCES.marketing.id,
    action: RESOURCES.marketing.actions.viewcamp,
  });

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Campaign name',
        cell: ({ row }: { row: { original: MarketingCampaignResponse } }) => {
          const canOpen = canEdit || canView;
          const campaignPath = `${location.pathname}/${row.original.id}`;
          const name = row.original.name ?? '';
          // TextLink's label wraps in nested flex containers internally, so
          // CSS truncation never kicks in for a name with no spaces to break
          // on (flex items don't shrink below their content's min-width).
          // Truncate the string itself instead, and rely on the tooltip for
          // the full name.
          const MAX_NAME_LENGTH = 45;
          const isLongName = name.length > MAX_NAME_LENGTH;
          const displayName = isLongName
            ? `${name.slice(0, MAX_NAME_LENGTH)}…`
            : name;

          const link = (
            <TextLink
              href={canOpen ? campaignPath : '#'}
              label={displayName}
              onClick={(event) => {
                // Let the browser handle new-tab/new-window clicks natively.
                if (
                  event.metaKey ||
                  event.ctrlKey ||
                  event.shiftKey ||
                  event.altKey
                ) {
                  return;
                }

                event.preventDefault();

                if (canOpen) {
                  navigate(campaignPath);
                }
              }}
            />
          );

          if (!isLongName) {
            return link;
          }

          // TextLink's own tooltipContent prop always renders an info icon
          // next to the label - wrap it in a plain Tooltip instead so the
          // full name shows on hover/keyboard focus with no icon.
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger className="ds-min-w-0 ds-max-w-full">
                  {link}
                </TooltipTrigger>
                {/* The tooltip's content box has a fixed max-width and
                    hides overflow, so a name with no spaces to wrap on
                    needs an explicit word-break to avoid being clipped. */}
                <NextTooltipContent style={{ wordBreak: 'break-all' }}>
                  {name}
                </NextTooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        },
      },
      {
        accessorKey: 'provider',
        header: 'Tracking Provider',
        cell: ({ row }: { row: { original: MarketingCampaignResponse } }) => {
          // The committed schema's provider enum predates Google Ads, which the
          // API already returns. Widen until the schema is regenerated.
          const provider = row.original.provider as string | undefined;
          if (provider === 'server_to_server') {
            return (
              options.find(
                (option) =>
                  option.value ===
                  row.original.provider_config?.server_to_server
                    ?.tracking_template_id
              )?.label ?? ''
            );
          } else if (provider === 'meta' || provider === 'google') {
            return RECRUITMENT_MARKETING_PROVIDER_DETAILS[provider].label;
          } else if (provider === 'no_tracking') {
            return 'No Tracking';
          } else {
            return provider ?? '';
          }
        },
      },
      {
        id: 'utm_parameters',
        header: 'UTM Parameters',
        accessorFn: (row: MarketingCampaignResponse) =>
          [row.utm_source, row.utm_campaign, row.utm_medium, row.utm_content]
            .filter((tag) => tag)
            .join(', '),
        cell: ({ row }: { row: { original: MarketingCampaignResponse } }) => (
          <div className="flex flex-row flex-wrap gap-1">
            {[
              { prefix: 'src', value: row.original.utm_source },
              { prefix: 'cmp', value: row.original.utm_campaign },
              { prefix: 'mdm', value: row.original.utm_medium },
              { prefix: 'cnt', value: row.original.utm_content },
            ]
              .filter(({ value }) => value)
              .map(({ prefix, value }) => (
                <SelectBadge
                  key={prefix}
                  label={prefix}
                  text={value}
                  onClick={() => {}}
                  isDisabled
                />
              ))}
          </div>
        ),
      },
      {
        accessorKey: 'event_configuration',
        header: 'Event Configuration',
        cell: ({ row }: { row: { original: MarketingCampaignResponse } }) => {
          const doubleOptIn =
            row.original.track_registration_double_opt_in_event;
          const doubleOptInAmount =
            row.original.recruitment_cost_per_double_opt_in_event;
          const complete = row.original.track_complete_participations_event;
          const completeUpTo =
            row.original.track_complete_participations_event_max_value;

          const values = [
            {
              prefix: 'Double-opt in',
              value: doubleOptIn
                ? doubleOptInAmount
                  ? `${doubleOptInAmount.value} ${doubleOptInAmount.currency_code}`
                  : '-'
                : undefined,
            },
            {
              prefix: 'Complete',
              value:
                complete &&
                (completeUpTo > 1 ? `Up to ${completeUpTo}` : 'Only 1st'),
            },
          ].filter(({ value }) => value);

          return (
            <div className="flex flex-row flex-wrap gap-1">
              {values.length > 0
                ? values.map(({ prefix, value }) => (
                    <SelectBadge
                      key={prefix}
                      label={prefix}
                      text={value}
                      onClick={() => {}}
                      isDisabled
                    />
                  ))
                : '-'}
            </div>
          );
        },
      },
      {
        accessorKey: 'panelist_starting_balance_amount',
        header: 'Starting Balance (TP)',
        cell: ({ row }: { row: { original: MarketingCampaignResponse } }) => {
          const amount = row.original.panelist_starting_balance_amount;
          return amount > 0 ? amount : '-';
        },
      },
    ],
    [canEdit, canView, location.pathname, navigate, options]
  );

  const table = useReactTable({
    data: filteredCampaignsData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableSortingRemoval: false,
    getRowId: (row) =>
      `${row.id}-${row.name}-${row.advertiser_id}-${row.utm_source}-${row.utm_campaign}-${row.utm_medium}-${row.utm_content}`,
  });

  useEffect(() => {
    table.resetPageIndex();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedItems]);

  if (
    campaignsLoading ||
    campaignsFetching ||
    trackingOptionsLoading ||
    trackingOptionsFetching
  ) {
    return <Spinner />;
  }

  if (campaignsError) {
    return <div>Error loading campaigns: {campaignsError.message}</div>;
  }

  return (
    <div>
      <TableToolbar
        leftSlot={
          <SearchInput
            placeholder="Search for campaign name"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        }
        centerSlot={[
          <SelectMenuProvider
            key="tracking-filter"
            isMultiSelect={true}
            onSelectedChange={handleSelectedTrackingChange}
            selectedItems={selectedItems}
          >
            <SelectMenu
              triggerProps={{
                type: SelectMenuTriggerType.MultiSelectButton,
                props: {
                  label: 'Tracking provider',
                },
              }}
            >
              <SelectMenuContent className="ds-z-[500]">
                <MenuInput placeholder="Search tracking providers" />
                <MenuEmpty>No results.</MenuEmpty>
                <MenuItems>
                  {options.map((option) => (
                    <SelectMenuItem
                      key={option.value}
                      label={option.label}
                      value={option.value}
                    />
                  ))}
                </MenuItems>
              </SelectMenuContent>
            </SelectMenu>
          </SelectMenuProvider>,
        ]}
        rightSlot={[
          <NextButton
            key="add-tracking"
            text="Add tracking"
            variant="primary"
            iconProps={{
              name: 'Plus',
            }}
            onClick={() => {
              navigate(
                location.pathname.replace(
                  'marketingCampaigns',
                  'createMarketingCampaign'
                )
              );
            }}
          />,
        ]}
      />
      <Table variant="striped" className="ds-table-fixed ds-w-full">
        <THead>
          {table.getHeaderGroups().map((headerGroup) => (
            <THeadRow key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => (
                <THeaderCell
                  key={header.id}
                  sortDirection={
                    header.column.getIsSorted() as 'asc' | 'desc' | 'none'
                  }
                  onClick={
                    index === 0
                      ? header.column.getToggleSortingHandler()
                      : undefined
                  }
                  isColumnSortable={index === 0}
                  className={
                    header.column.id === 'name'
                      ? 'ds-min-w-[257px] ds-max-w-[417px]'
                      : undefined
                  }
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </THeaderCell>
              ))}
            </THeadRow>
          ))}
        </THead>
        <TBody>
          {filteredCampaignsData.length === 0 ? (
            <Tr>
              <TCellData colSpan={columns.length}>
                No results for your search
              </TCellData>
            </Tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TCellData
                    key={cell.id}
                    className={
                      cell.column.id === 'name'
                        ? 'ds-min-w-[257px] ds-max-w-[417px]'
                        : undefined
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TCellData>
                ))}
              </Tr>
            ))
          )}
        </TBody>
      </Table>
      {filteredCampaignsData.length >= 10 && (
        <Pagination
          availableRowsPerPage={[10, 20, 50, 100]}
          currentPage={table.getState().pagination.pageIndex + 1}
          decoratorText={{
            page: 'Page',
            pageOf: 'of',
            rowsPerPage: 'Rows per page',
          }}
          onChangePage={function DV(page) {
            table.setPageIndex(page - 1);
          }}
          onRowsPerPageChange={function DV(rowsPerPage) {
            table.setPageSize(rowsPerPage);
          }}
          pageCount={table.getPageCount()}
          rowsPerPage={table.getState().pagination.pageSize}
          showPageSelectDropdown
          totalItems={table.getPrePaginationRowModel().rows.length}
        />
      )}
    </div>
  );
}
